import { buttonsSaveCancel } from '../../../webmodule-common/other/ui/modal-confirmation';
import { customElement } from 'lit/decorators.js';
import { DataBinding } from '../../../webmodule-common/other/ui/databinding/databinding';
import { DataTracker } from '../../../webmodule-common/other/ui/databinding/data-tracker';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow';
import { isEmptyOrSpace } from '../../../webmodule-common/other/ui/string-helper-functions';
import { ModalDialog } from '../../../webmodule-common/other/ui/modal-base';
import { QuoteContainerManager } from '../data/quote-container';
import { QuoteItem, QuoteItemPrice } from '../../api/dealer-api-interface-quote';
import { QuoteItemContainer } from '../data/quote-item-container';
import { QuoteItemView } from './quote-item-view';
import { showValidations } from '../../../webmodule-common/other/ui/modal-validationhandler';
import { Snippet } from '../../../webmodule-common/other/ui/events';
import { TemplateResult } from 'lit';
import { tlang } from '../../../webmodule-common/other/language/lang';

@customElement('wm-lineitemdialog')
export abstract class LineItemDialog extends ModalDialog {
  quoteManager: QuoteContainerManager;
  quoteItemContainer: QuoteItemContainer;
  dataBinding: DataBinding;
  dataTracker: DataTracker;
  ok = false;
  forceReadonly: boolean;

  constructor(quoteManager: QuoteContainerManager, quoteItemContainer: QuoteItemContainer, forceReadonly: boolean) {
    super();
    this.quoteManager = quoteManager;
    this.quoteItemContainer = quoteItemContainer;
    this.forceReadonly = forceReadonly;
    this.dataBinding = new DataBinding(this.ui);
    this.dataTracker = new DataTracker(this.dataBinding);
  }

  protected getQuoteItem(): QuoteItem {
    return this.quoteItemContainer.item;
  }

  protected getQuoteItemPrice(): QuoteItemPrice {
    return this.quoteItemContainer.price;
  }

  async save(): Promise<boolean> {
    this.prepareForSave();
    const errors = await this.getValidationErrors();
    if (errors.length != 0) {
      await showValidations(errors);
      this.quoteItemContainer =
        this.quoteManager.restoreQuoteItemFromBackup(this.quoteItemContainer.item.id) ?? this.quoteItemContainer;
      this.dataTracker.resetEditorValue();
      return false;
    } else if (isAutoSaving()) await this.internalSave();
    return true;
  }

  protected abstract getTitle(): Snippet;

  protected get modalSize() {
    return 'modal-md';
  }

  protected prepareForSave(): void {
    if (this.forceReadonly) return;
    this.dataTracker.applyChangeToValue();
  }

  protected async internalSave() {
    // do nothing here, override in subclass
  }

  protected async getValidationErrors(): Promise<string[]> {
    const errors: string[] = [];
    if (isEmptyOrSpace(this.dataBinding.getValue('title'))) {
      const title = await this.getTitle();
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      errors.push(tlang`${title.toString()} cannot have an empty Title`);
    }

    return errors;
  }

  protected footerTemplate(): TemplateResult {
    const saveEvent = async () => {
      if (await this.save()) await this.hideModal();
    };
    const closeEvent = () => {
      this.hideModal();
    };
    const buttons = buttonsSaveCancel();

    const template = this.forceReadonly
      ? this.createCancelButton()
      : this.createConfirmCancelButtons(buttons.ok, saveEvent, buttons.cancel, closeEvent);
    return template;
  }
}

export abstract class LineItemView extends QuoteItemView {
  public get hasPropertyDialog(): boolean {
    return true;
  }

  public get hasModalEditDialog(): boolean {
    return true;
  }
}
